import React, { useCallback, useState, Fragment } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AppsIcon from '@atlaskit/icon/core/apps';
import { Text } from '@atlaskit/primitives/compiled';
import { IconButton } from '@atlaskit/button/new';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';

import { useRouteActions, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { FallbackableImage } from '@confluence/fallbackable-image';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useSpacePersistentExpansion } from '@confluence/collapsible-section/entry-points/useSpacePersistentExpansion';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';

import { SPACE_APPS_METRIC } from './perf.config';
import { isConnectApp, isForgeApp } from './space-apps-helpers';
import type { SpaceNavigationQuery_spaceSidebarLinks_main } from './__types__/SpaceNavigationQuery';
import { SpaceSidebarLinkType } from './__types__/SpaceNavigationQuery';

type SpaceAppsProps = {
	isSpaceAdmin: boolean;
	allAppLinks: any[];
	allVisibleAppLinks: any[];
	spaceKey: string;
};

export const SpaceApps = ({
	isSpaceAdmin,
	allAppLinks,
	allVisibleAppLinks,
	spaceKey,
}: SpaceAppsProps) => {
	const { isExpanded, onExpansionToggle } = useSpacePersistentExpansion({
		sectionName: 'apps',
	});
	const [isOpenAppsConfiguration, setOpenAppsConfiguration] = useState(false);
	const hasVisibleAppLinks = allVisibleAppLinks.length !== 0;

	if (allAppLinks.length === 0 || (!hasVisibleAppLinks && !isSpaceAdmin)) {
		return null;
	}

	const pageSegmentLoadCustomData = {
		connectApps: allVisibleAppLinks.filter((a) => isConnectApp(a)).length,
		forgeApps: allVisibleAppLinks.filter((a) => isForgeApp(a)).length,
	};

	return (
		<>
			<PageSegmentLoadEnd
				key={spaceKey}
				metric={SPACE_APPS_METRIC}
				customData={pageSegmentLoadCustomData}
			/>
			<ExpandableMenuItem
				isExpanded={isExpanded}
				onExpansionToggle={onExpansionToggle}
				testId="space-apps"
			>
				<ExpandableMenuItemTrigger
					elemBefore={<AppsIcon label="" color="currentColor" />}
					actionsOnHover={
						isSpaceAdmin && (
							<Tooltip content={<FormattedMessage {...i18n.editButtonTooltip} />} position="bottom">
								<IconButton
									icon={(iconProps) => <ShowMoreIcon {...iconProps} size="small" />}
									label={<FormattedMessage {...i18n.editButtonLabel} />}
									onClick={() => setOpenAppsConfiguration(true)}
									spacing="compact"
									appearance="subtle"
								/>
							</Tooltip>
						)
					}
				>
					<FormattedMessage {...i18n.title} />
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{hasVisibleAppLinks ? (
						<MenuList>
							{allVisibleAppLinks.map((link) => (
								<AppLink key={link.id} link={link} />
							))}
						</MenuList>
					) : (
						<Text color="color.text.subtle">
							<FormattedMessage {...i18n.noAppsMessage} />
						</Text>
					)}
				</ExpandableMenuItemContent>
			</ExpandableMenuItem>

			<ModalTransition>
				{isOpenAppsConfiguration ? (
					<LazySpaceAppsConfiguration
						links={allAppLinks}
						spaceKey={spaceKey}
						onCancel={() => setOpenAppsConfiguration(false)}
						onSave={() => setOpenAppsConfiguration(false)}
					/>
				) : (
					<Fragment />
				)}
			</ModalTransition>
		</>
	);
};

type AppLinkProps = {
	link: SpaceNavigationQuery_spaceSidebarLinks_main;
};

const AppLink = ({ link }: AppLinkProps) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { id, icon, url, title, type, styleClass } = link;
	const isMatchingCurrentUrlArgs = {
		selector: (actualUrl: string | undefined) => actualUrl === url,
	};
	const matchesUrl = useRouteUrl(isMatchingCurrentUrlArgs);
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				source: 'sidebar',
				actionSubjectId: 'appLink',
				attributes: {
					appTitle: title,
				},
			},
		}).fire();
	};

	/**
	 * Injecting the `styleClass` using a callback ref.
	 *
	 * This could be a `useLayoutEffect` too, but done this way
	 * for consistency with `AppsMenuContent.tsx`
	 *
	 * A `useLayoutEffect` might be desirable if we need to worry about
	 * removing the styles, such as if `styleClass` can change.
	 *
	 * From what I can tell `styleClass` cannot change so this doesn't matter.
	 *
	 * Also, a callback ref should be called before any effects (better for TTVC?)
	 */
	const injectStyleClass = useCallback(
		(element: HTMLElement | null) => {
			if (element === null) {
				return;
			}

			if (typeof styleClass !== 'string' || styleClass.length === 0) {
				return;
			}

			const classList = styleClass.split(' ');

			element.classList.add(...classList);
		},
		[styleClass],
	);

	const linkItem = (
		<LinkMenuItem
			key={id}
			ref={injectStyleClass}
			elemBefore={
				<FallbackableImage
					data-vc="space-apps-link-icon"
					src={icon?.path}
					width="16"
					height="16"
					fallback={<DefaultAppIcon label="" />}
					role="presentation"
				/>
			}
			isSelected={Boolean(url && matchesUrl)}
			href={url || ''}
			onClick={onClick}
		>
			{title}
		</LinkMenuItem>
	);

	return type === SpaceSidebarLinkType.FORGE && !renderServerPlaceholder ? (
		<ForgeKeyboardShortcutVisualizer key={id} module={link as any}>
			{linkItem}
			{url && <ForgeKeyboardShortcut key={id} module={link as any} action={() => push(url)} />}
		</ForgeKeyboardShortcutVisualizer>
	) : (
		linkItem
	);
};

const LazySpaceAppsConfiguration = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceAppsConfiguration" */ './SpaceAppsConfiguration'
			)
		).SpaceAppsConfiguration,
});

const i18n = defineMessages({
	title: {
		id: 'side-navigation.space-apps.header',
		defaultMessage: 'Space apps',
		description: 'Header for a list of apps in a space',
	},
	editButtonLabel: {
		id: 'side-navigation.space-apps.edit.label',
		defaultMessage: 'Edit apps',
		description: 'Label for the edit space apps button',
	},
	editButtonTooltip: {
		id: 'side-navigation.space-apps.edit.tooltip',
		defaultMessage: 'Reorder, show and hide apps',
		description: 'Tip describing what a edit button will do',
	},
	noAppsMessage: {
		id: 'side-navigation.space-apps.no-space-apps.message',
		defaultMessage: 'No apps showing in the sidebar',
		description: 'Message when apps section is empty because a space admin has hidden all apps',
	},
});
